import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
  LogOut,
  CalendarDays,
  FileText,
  Loader2,
  AlertCircle,
  Trash2,
  PenLine,
  Plus,
  X,
  Link2,
  Check,
  GripVertical,
  Upload,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAuth } from '@/contexts/AuthContext'
import { apiUrl } from '@/lib/api'
import { NewsContent } from '@/components/ui/NewsContent'
import type { Booking, BookingStatus } from '@brochure-cloudflare/shared'

const MOCK_BOOKINGS: Booking[] = [
  {
    id: 1,
    name: 'Giulia Rossi',
    email: 'giulia.rossi@example.com',
    phone: '+39 333 123 4567',
    service: 'Chirurgia del Seno',
    preferredDate: '2026-09-15',
    preferredTime: '10:30',
    notes: 'Prima visita per mastoplastica additiva.',
    status: 'pending',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 2,
    name: 'Marco Bianchi',
    email: 'marco.bianchi@example.com',
    phone: '+39 338 765 4321',
    service: 'Medicina Estetica Viso',
    preferredDate: '2026-09-16',
    preferredTime: '15:00',
    notes: 'Valutazione trattamento filler labbra.',
    status: 'confirmed',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 3,
    name: 'Anna Verdi',
    email: 'anna.verdi@example.com',
    phone: '+39 340 987 6543',
    service: 'Liposuzione',
    preferredDate: '2026-09-18',
    preferredTime: '09:00',
    notes: 'Richiesta informazioni su liposuzione addome.',
    status: 'pending',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 4,
    name: 'Luca Neri',
    email: 'luca.neri@example.com',
    phone: '+39 347 111 2233',
    service: 'Rinoplastica',
    preferredDate: '2026-09-20',
    preferredTime: '11:00',
    notes: 'Secondo consulto dopo visita precedente.',
    status: 'confirmed',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 5,
    name: 'Francesca Blu',
    email: 'francesca.blu@example.com',
    phone: '+39 349 444 5566',
    service: 'Trattamenti Laser',
    preferredDate: '2026-09-22',
    preferredTime: '14:30',
    notes: 'Laser per macchie cutanee sul viso.',
    status: 'pending',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
]

const statusDotClasses: Record<BookingStatus, string> = {
  pending: 'bg-white/40',
  confirmed: 'bg-green-500',
  cancelled: 'bg-white/30',
}

type TabKey = 'consultations' | 'blog'

export function AdminDashboard() {
  const { t } = useTranslation('admin')
  const { token, logout } = useAuth()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<TabKey>('consultations')
  const [bookings, setBookings] = useState<Booking[]>([...MOCK_BOOKINGS])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    fetchBookings()
  }, [])

  const fetchBookings = async () => {
    setLoading(true)
    setError('')
    try {
      const res = await fetch(apiUrl('/api/bookings'), {
        headers: { Authorization: `Bearer ${token}` },
      })

      if (res.status === 401) {
        logout()
        navigate('/login')
        return
      }

      if (!res.ok) throw new Error(t('consultations.loadError', 'Errore di caricamento'))

      const data = (await res.json()) as { bookings: Booking[] }
      setBookings(data.bookings.length > 0 ? data.bookings : [...MOCK_BOOKINGS])
    } catch (err) {
      setError(err instanceof Error ? err.message : t('consultations.loadError', 'Errore di caricamento'))
    } finally {
      setLoading(false)
    }
  }

  const confirmBooking = async (id: number) => {
    const res = await fetch(apiUrl(`/api/bookings/${id}`), {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ status: 'confirmed' }),
    })

    if (!res.ok) {
      const data = await res.json()
      alert(data.error || t('consultations.updateError', 'Errore aggiornamento'))
      return
    }

    setBookings((prev) => prev.map((b) => (b.id === id ? { ...b, status: 'confirmed' as const } : b)))
  }

  const deleteBooking = async (id: number) => {
    if (!confirm(t('consultations.deleteConfirm', 'Eliminare questa prenotazione?'))) return

    const res = await fetch(apiUrl(`/api/bookings/${id}`), {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    })

    if (!res.ok) {
      const data = await res.json()
      alert(data.error || t('consultations.deleteError', 'Errore eliminazione'))
      return
    }

    setBookings((prev) => prev.filter((b) => b.id !== id))
  }

  const tabs: { key: TabKey; label: string; icon: React.ElementType }[] = [
    {
      key: 'consultations',
      label: t('tabs.consultations', 'Consulenze'),
      icon: CalendarDays,
    },
    { key: 'blog', label: t('tabs.blog', 'Blog'), icon: FileText },
  ]

  return (
    <main className="min-h-screen bg-ink-deep pt-24 pb-12 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <header className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="font-display text-3xl font-bold text-white md:text-4xl">
              {t('title', 'Area Riservata')}
            </h1>
            <p className="mt-2 max-w-2xl text-white/70">
              {t('subtitle', 'Gestisci consulenze e contenuti del blog.')}
            </p>
          </div>
          <button
            type="button"
            onClick={logout}
            className="inline-flex items-center gap-2 self-start rounded-full border border-white/20 bg-white/5 px-4 py-2 text-xs font-semibold uppercase tracking-[0.15em] text-white transition-colors hover:bg-white/10"
          >
            <LogOut className="h-4 w-4" aria-hidden="true" />
            {t('logout', 'Esci')}
          </button>
        </header>

        <nav
          aria-label={t('aria.tabNavigation', 'Navigazione sezioni')}
          className="mb-8 flex gap-2 border-b border-white/10"
        >
          {tabs.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              type="button"
              onClick={() => setActiveTab(key)}
              className={cn(
                'inline-flex items-center gap-2 border-b-2 px-4 py-3 text-xs font-semibold uppercase tracking-[0.15em] transition-colors',
                activeTab === key
                  ? 'border-primary text-white'
                  : 'border-transparent text-white/60 hover:text-white',
              )}
              aria-current={activeTab === key ? 'page' : undefined}
            >
              <Icon className="h-4 w-4" aria-hidden="true" />
              {label}
            </button>
          ))}
        </nav>

        {activeTab === 'consultations' && (
          <ConsultationsSection
            bookings={bookings}
            loading={loading}
            error={error}
            onConfirm={confirmBooking}
            onDelete={deleteBooking}
          />
        )}
        {activeTab === 'blog' && <BlogSection />}
      </div>
    </main>
  )
}

function ConsultationsSection({
  bookings,
  loading,
  error,
  onConfirm,
  onDelete,
}: {
  bookings: Booking[]
  loading: boolean
  error: string
  onConfirm: (id: number) => void
  onDelete: (id: number) => void
}) {
  const { t } = useTranslation('admin')

  if (loading) {
    return (
      <div className="flex h-96 flex-col items-center justify-center gap-3 text-white/70">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-sm uppercase tracking-wider">{t('loading', 'Caricamento...')}</p>
      </div>
    )
  }

  return (
    <section className="space-y-6" aria-labelledby="consultations-title">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h2 id="consultations-title" className="font-display text-2xl font-bold text-white">
          {t('consultations.title', 'Richieste di consulenza')}
        </h2>
        <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-medium text-white/70">
          {bookings.length} {bookings.length === 1 ? t('consultations.singular', 'richiesta') : t('consultations.plural', 'richieste')}
        </span>
      </div>

      {error && (
        <div className="flex items-center gap-3 rounded-xl border border-red-400/20 bg-red-500/10 p-4 text-red-300">
          <AlertCircle className="h-5 w-5 shrink-0" aria-hidden="true" />
          <p className="text-sm">{error}</p>
        </div>
      )}

      <div className="overflow-hidden rounded-2xl border border-white/10 bg-ink shadow-xl">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-white/10 bg-ink-deep text-xs uppercase tracking-wider text-white/60">
              <tr>
                <th className="px-5 py-4 font-medium">{t('consultations.columns.name', 'Nome')}</th>
                <th className="px-5 py-4 font-medium">{t('consultations.columns.contact', 'Contatto')}</th>
                <th className="px-5 py-4 font-medium">{t('consultations.columns.service', 'Servizio')}</th>
                <th className="px-5 py-4 font-medium">{t('consultations.columns.date', 'Data preferita')}</th>
                <th className="px-5 py-4 font-medium">{t('consultations.columns.status', 'Stato')}</th>
                <th className="px-5 py-4 font-medium">{t('consultations.columns.actions', 'Azioni')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {bookings.map((booking) => (
                <tr key={booking.id} className="transition-colors hover:bg-white/[0.02]">
                  <td className="px-5 py-4 font-medium text-white">{booking.name}</td>
                  <td className="px-5 py-4 text-white/70">
                    <div className="flex flex-col gap-0.5">
                      <a href={`mailto:${booking.email}`} className="hover:text-primary transition-colors">
                        {booking.email}
                      </a>
                      {booking.phone && (
                        <a href={`tel:${booking.phone}`} className="hover:text-primary transition-colors">
                          {booking.phone}
                        </a>
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-4 text-white/70">{booking.service || '-'}</td>
                  <td className="px-5 py-4 text-white/70">
                    {booking.preferredDate ? (
                      <>
                        <span className="block">{booking.preferredDate}</span>
                        {booking.preferredTime && (
                          <span className="text-white/50">{booking.preferredTime}</span>
                        )}
                      </>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className="px-5 py-4">
                    <span
                      className={cn(
                        'inline-block h-2.5 w-2.5 rounded-full',
                        statusDotClasses[booking.status],
                      )}
                      aria-hidden="true"
                    />
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-2">
                      {booking.status === 'pending' && (
                        <button
                          type="button"
                          onClick={() => onConfirm(booking.id)}
                          className="inline-flex items-center justify-center rounded-lg border border-green-500/20 bg-green-500/10 p-2 text-green-400 transition-colors hover:bg-green-500/20"
                          aria-label={t('consultations.confirm', 'Conferma')}
                        >
                          <Check className="h-4 w-4" aria-hidden="true" />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => onDelete(booking.id)}
                        className="inline-flex items-center justify-center rounded-lg border border-white/10 p-2 text-white/60 transition-colors hover:border-red-400/30 hover:bg-red-500/10 hover:text-red-400"
                        aria-label={t('consultations.delete', 'Elimina')}
                      >
                        <Trash2 className="h-4 w-4" aria-hidden="true" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  )
}

type ParagraphBlock = {
  id: string
  title: string
  body: string
}

function BlogSection() {
  const { t } = useTranslation('admin')

  const [title, setTitle] = useState('')
  const [coverImage, setCoverImage] = useState('')
  const [videoUrl, setVideoUrl] = useState('')
  const [paragraphs, setParagraphs] = useState<ParagraphBlock[]>([
    { id: crypto.randomUUID(), title: '', body: '' },
  ])
  const [submitting, setSubmitting] = useState(false)
  const [success, setSuccess] = useState(false)
  const [formError, setFormError] = useState('')

  const content = useMemo(() => {
    return paragraphs
      .filter((p) => p.title.trim() || p.body.trim())
      .map((p) => {
        const title = p.title.trim()
        const body = p.body.trim()
        if (title && body) return `## ${title}\n\n${body}`
        if (title) return `## ${title}`
        return body
      })
      .filter(Boolean)
      .join('\n\n')
  }, [paragraphs])

  const addParagraph = () => {
    setParagraphs((prev) => [...prev, { id: crypto.randomUUID(), title: '', body: '' }])
  }

  const removeParagraph = (index: number) => {
    setParagraphs((prev) => prev.filter((_, i) => i !== index))
  }

  const updateParagraphTitle = (index: number, value: string) => {
    setParagraphs((prev) => prev.map((p, i) => (i === index ? { ...p, title: value } : p)))
  }

  const updateParagraphBody = (index: number, value: string) => {
    setParagraphs((prev) => prev.map((p, i) => (i === index ? { ...p, body: value } : p)))
  }

  const moveParagraph = (fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) return
    setParagraphs((prev) => {
      const next = [...prev]
      const [moved] = next.splice(fromIndex, 1)
      next.splice(toIndex, 0, moved)
      return next
    })
  }

  const resetForm = () => {
    setTitle('')
    setCoverImage('')
    setVideoUrl('')
    setParagraphs([{ id: crypto.randomUUID(), title: '', body: '' }])
    setFormError('')
    setSuccess(false)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setFormError('')
    setSuccess(false)

    const validParagraphs = paragraphs.filter((p) => p.title.trim() || p.body.trim())

    if (!title.trim()) {
      setFormError(t('blog.validation.title', 'Inserisci il titolo dell’articolo.'))
      return
    }
    if (!coverImage.trim()) {
      setFormError(t('blog.validation.coverImage', 'Inserisci l’immagine di copertina.'))
      return
    }
    if (validParagraphs.length === 0) {
      setFormError(t('blog.validation.paragraphs', 'Aggiungi almeno un paragrafo.'))
      return
    }

    // Mock: nessuna chiamata API per ora.
    setSubmitting(true)
    setTimeout(() => {
      setSuccess(true)
      setSubmitting(false)
      setTimeout(() => resetForm(), 2000)
    }, 600)
  }

  return (
    <section aria-labelledby="blog-title">
      <h2 id="blog-title" className="font-display text-2xl font-bold text-white mb-6">
        {t('blog.newArticle', 'Nuovo articolo')}
      </h2>

      <form onSubmit={handleSubmit} className="rounded-2xl border border-white/10 bg-ink p-6 md:p-8 shadow-xl space-y-6">
        {formError && (
          <div className="flex items-center gap-3 rounded-xl border border-red-400/20 bg-red-500/10 p-4 text-red-300">
            <AlertCircle className="h-5 w-5 shrink-0" aria-hidden="true" />
            <p className="text-sm">{formError}</p>
          </div>
        )}

        {success && (
          <div className="flex items-center gap-3 rounded-xl border border-green-500/20 bg-green-500/10 p-4 text-green-300">
            <Check className="h-5 w-5 shrink-0" aria-hidden="true" />
            <p className="text-sm">{t('blog.success', 'Articolo pubblicato con successo.')}</p>
          </div>
        )}

        <div className="space-y-2">
          <label htmlFor="article-title" className="block text-xs font-semibold uppercase tracking-widest text-white/50">
            {t('blog.fields.title', 'Titolo')}
          </label>
          <input
            id="article-title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={submitting}
            placeholder={t('blog.placeholders.title', 'Titolo dell’articolo')}
            className="block w-full rounded-xl border border-white/10 bg-cream/5 px-4 py-3 text-white placeholder:text-white/30 transition-colors focus:border-primary focus:bg-cream/10 focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:opacity-50"
          />
        </div>

        <CoverImageUpload value={coverImage} onChange={setCoverImage} disabled={submitting} />

        <div className="space-y-2">
          <label htmlFor="article-video" className="block text-xs font-semibold uppercase tracking-widest text-white/50">
            {t('blog.fields.videoUrl', 'Video YouTube (opzionale)')}
          </label>
          <div className="relative">
            <Link2 className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-white/30" aria-hidden="true" />
            <input
              id="article-video"
              type="url"
              value={videoUrl}
              onChange={(e) => setVideoUrl(e.target.value)}
              disabled={submitting}
              placeholder="https://youtube.com/..."
              className="block w-full rounded-xl border border-white/10 bg-cream/5 pl-12 pr-4 py-3 text-white placeholder:text-white/30 transition-colors focus:border-primary focus:bg-cream/10 focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:opacity-50"
            />
          </div>
        </div>

        <div className="space-y-3">
          <label className="block text-xs font-semibold uppercase tracking-widest text-white/50">
            {t('blog.fields.paragraphs', 'Paragrafi')}
          </label>
          {paragraphs.map((paragraph, index) => (
            <ParagraphItem
              key={paragraph.id}
              index={index}
              title={paragraph.title}
              body={paragraph.body}
              onTitleChange={(v) => updateParagraphTitle(index, v)}
              onBodyChange={(v) => updateParagraphBody(index, v)}
              onRemove={() => removeParagraph(index)}
              canRemove={paragraphs.length > 1}
              moveParagraph={moveParagraph}
            />
          ))}
          <button
            type="button"
            onClick={addParagraph}
            disabled={submitting}
            className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs font-bold uppercase tracking-[0.15em] text-white transition-colors hover:bg-white/10 disabled:opacity-50"
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
            {t('blog.addParagraph', 'Aggiungi paragrafo')}
          </button>
        </div>

        <div className="space-y-3">
          <p className="text-xs font-semibold uppercase tracking-widest text-white/50">
            {t('blog.preview', 'Anteprima articolo')}
          </p>
          <div className="rounded-xl border border-white/10 bg-ink-deep/60 p-6 md:p-8 max-h-[600px] overflow-y-auto">
            {content.trim() ? (
              <NewsContent content={content} />
            ) : (
              <p className="text-sm text-white/40 italic">
                {t('blog.emptyPreview', 'L’anteprima apparirà qui man mano che scrivi i paragrafi.')}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3 pt-2">
          <button
            type="submit"
            disabled={submitting}
            className={cn(
              'inline-flex items-center gap-2 rounded-full bg-primary px-8 py-4 text-sm font-bold text-ink shadow-lg transition-colors hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-70',
            )}
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {t('blog.publishing', 'Pubblicazione...')}
              </>
            ) : (
              <>
                <PenLine className="h-4 w-4" />
                {t('blog.publish', 'Pubblica articolo')}
              </>
            )}
          </button>
          <button
            type="button"
            onClick={resetForm}
            disabled={submitting}
            className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-transparent px-6 py-4 text-sm font-bold text-white transition-colors hover:bg-white/5 disabled:opacity-50"
          >
            {t('blog.reset', 'Annulla')}
          </button>
        </div>
      </form>
    </section>
  )
}

function ParagraphItem({
  index,
  title,
  body,
  onTitleChange,
  onBodyChange,
  onRemove,
  canRemove,
  moveParagraph,
}: {
  index: number
  title: string
  body: string
  onTitleChange: (value: string) => void
  onBodyChange: (value: string) => void
  onRemove: () => void
  canRemove: boolean
  moveParagraph: (from: number, to: number) => void
}) {
  const { t } = useTranslation('admin')
  const [isDragOver, setIsDragOver] = useState(false)

  return (
    <div
      draggable
      onDragStart={(e) => {
        if (!(e.target as HTMLElement).closest('[data-drag-handle]')) {
          e.preventDefault()
          return
        }
        e.dataTransfer.setData('text/plain', String(index))
        e.dataTransfer.effectAllowed = 'move'
      }}
      onDragOver={(e) => {
        e.preventDefault()
        setIsDragOver(true)
      }}
      onDragLeave={() => setIsDragOver(false)}
      onDrop={(e) => {
        e.preventDefault()
        const fromIndex = Number(e.dataTransfer.getData('text/plain'))
        moveParagraph(fromIndex, index)
        setIsDragOver(false)
      }}
      className={cn(
        'rounded-xl border border-white/10 bg-ink-deep/40 p-4 transition-colors',
        isDragOver && 'bg-white/10 border-primary/30',
      )}
    >
      <div className="flex items-center gap-2">
        <span
          data-drag-handle
          className="cursor-grab p-2 text-white/30 hover:text-white/60 active:cursor-grabbing shrink-0"
          aria-label={t('blog.dragHandle', 'Trascina per riordinare')}
          title={t('blog.dragHandle', 'Trascina per riordinare')}
        >
          <GripVertical className="h-4 w-4" aria-hidden="true" />
        </span>
        <input
          type="text"
          value={title}
          onChange={(e) => onTitleChange(e.target.value)}
          placeholder={t('blog.placeholders.paragraph', 'Titolo paragrafo')}
          className="flex-1 rounded-xl border border-white/10 bg-cream/5 px-4 py-3 text-white placeholder:text-white/30 transition-colors focus:border-primary focus:bg-cream/10 focus:outline-none focus:ring-2 focus:ring-primary/20"
        />
        {canRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="inline-flex items-center justify-center rounded-lg border border-white/10 p-3 text-white/60 transition-colors hover:border-red-400/30 hover:bg-red-500/10 hover:text-red-400 shrink-0"
            aria-label={t('blog.removeParagraph', 'Rimuovi paragrafo')}
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      <textarea
        value={body}
        onChange={(e) => onBodyChange(e.target.value)}
        placeholder={t('blog.placeholders.paragraphBody', 'Testo del paragrafo')}
        rows={4}
        className="mt-3 block w-full rounded-xl border border-white/10 bg-cream/5 px-4 py-3 text-white placeholder:text-white/30 transition-colors focus:border-primary focus:bg-cream/10 focus:outline-none focus:ring-2 focus:ring-primary/20 font-light leading-relaxed"
      />
    </div>
  )
}

function CoverImageUpload({
  value,
  onChange,
  disabled,
}: {
  value: string
  onChange: (url: string) => void
  disabled?: boolean
}) {
  const { t } = useTranslation('admin')
  const { token, logout } = useAuth()
  const navigate = useNavigate()
  const inputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState('')
  const [mode, setMode] = useState<'upload' | 'url'>('upload')

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setUploading(true)
    setUploadError('')

    const formData = new FormData()
    formData.append('file', file)

    try {
      const res = await fetch(apiUrl('/api/upload'), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      })

      if (res.status === 401) {
        logout()
        navigate('/login')
        return
      }

      if (!res.ok) {
        const data = (await res.json()) as { error?: string }
        throw new Error(data.error || t('blog.uploadError', 'Errore durante il caricamento.'))
      }

      const data = (await res.json()) as { url: string }
      onChange(apiUrl(data.url))
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : t('blog.uploadError', 'Errore durante il caricamento.'))
    } finally {
      setUploading(false)
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  return (
    <div className="space-y-3">
      <label className="block text-xs font-semibold uppercase tracking-widest text-white/50">
        {t('blog.fields.coverImage', 'Immagine di copertina')}
      </label>

      {value ? (
        <div className="relative overflow-hidden rounded-xl border border-white/10 bg-ink-deep">
          <img
            src={value}
            alt={t('blog.coverPreview', 'Anteprima copertina')}
            className="h-48 w-full object-cover"
          />
          <button
            type="button"
            onClick={() => onChange('')}
            disabled={disabled || uploading}
            className="absolute right-2 top-2 rounded-full bg-ink-deep/80 p-1.5 text-white/80 hover:text-red-400 border border-white/10 transition-colors"
            aria-label={t('blog.removeCover', 'Rimuovi immagine')}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setMode('upload')}
              className={cn(
                'rounded-lg px-4 py-2 text-xs font-semibold uppercase tracking-wider transition-colors',
                mode === 'upload'
                  ? 'bg-white/10 text-white'
                  : 'text-white/50 hover:text-white hover:bg-white/5',
              )}
            >
              {t('blog.tabUpload', 'Carica')}
            </button>
            <button
              type="button"
              onClick={() => setMode('url')}
              className={cn(
                'rounded-lg px-4 py-2 text-xs font-semibold uppercase tracking-wider transition-colors',
                mode === 'url'
                  ? 'bg-white/10 text-white'
                  : 'text-white/50 hover:text-white hover:bg-white/5',
              )}
            >
              {t('blog.tabUrl', 'URL')}
            </button>
          </div>

          {mode === 'upload' ? (
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={disabled || uploading}
              className="flex w-full flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-white/20 bg-cream/5 px-4 py-10 text-white/60 transition-colors hover:border-primary/40 hover:bg-cream/10 disabled:opacity-50"
            >
              {uploading ? (
                <>
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                  <span className="text-sm">{t('blog.uploading', 'Caricamento...')}</span>
                </>
              ) : (
                <>
                  <Upload className="h-6 w-6" aria-hidden="true" />
                  <span className="text-sm">{t('blog.uploadCover', 'Carica immagine di copertina')}</span>
                </>
              )}
            </button>
          ) : (
            <div className="relative">
              <Link2 className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-white/30" aria-hidden="true" />
              <input
                type="url"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                disabled={disabled}
                placeholder="https://..."
                className="block w-full rounded-xl border border-white/10 bg-cream/5 pl-12 pr-4 py-3 text-white placeholder:text-white/30 transition-colors focus:border-primary focus:bg-cream/10 focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:opacity-50"
              />
            </div>
          )}
        </>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        disabled={disabled || uploading}
        className="sr-only"
        aria-label={t('blog.fields.coverImage', 'Immagine di copertina')}
      />

      {uploadError && (
        <p className="text-sm text-red-300">{uploadError}</p>
      )}
    </div>
  )
}
